import { modals } from '@mantine/modals'
import { useRouter } from '@v2/commons/utils/navigation'
import {
  authApiEndpoints,
  useChangeOrganizationMutation,
} from '@v2/domains/auth/api/auth.api'
import { setAuthInfo } from '@v2/domains/auth/store/auth.store'
import { resetAccountStoreAccount } from '@v2/domains/person/store/account/assignAccount.store'
import { resetLeadStoreLead } from '@v2/domains/person/store/leads/assignLead.store'
import { useTheme } from '@v2/domains/ui/hooks/useTheme.hook'
import { setGlobalIsLoading } from '@v2/domains/ui/store/ui.store'
import { ThemeType } from '@v2/domains/ui/ui.types'
import { rootApi } from '@v2/store/api/rootApi'
import { useState } from 'react'

import { BENESTREAM_ORG_ID } from 'src/common/config'
import { appLocalLogo } from 'src/common/libs'
import { cleanStringToBeUrlReadable } from 'src/default/helpers/clean-string'
import { setUserSession } from 'src/v2/domains/user/store/user.store'
import { useAppDispatch, useAppSelector } from 'src/v2/store/hooks'

import { verifyVendorUserForBCF } from '../utils/verifyVendorUserForBCF'

export const useChangeOrganizationProcess = () => {
  const router = useRouter()
  const dispatch = useAppDispatch()
  const refreshToken = useAppSelector((state) => state.auth.refreshToken)
  const [isChanging, setIsChanging] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const { loadTheme } = useTheme()

  const [changeOrganization] = useChangeOrganizationMutation()

  const handleChangeOrganization = async (
    organizationId: string,
    membershipId: string,
    personId?: string,
    responseRefreshToken?: string,
  ) => {
    if (!organizationId || !membershipId) {
      setError('Organization ID and Membership ID are required')
      return
    }

    setIsChanging(true)
    dispatch(setGlobalIsLoading(true))
    setError(null)

    try {
      const refreshResult = await changeOrganization({
        organizationId,
        membershipId,
        refreshToken: refreshToken || responseRefreshToken || '',
        refresh_token: refreshToken || responseRefreshToken || '',
      }).unwrap()

      dispatch(
        setAuthInfo({
          access_token: refreshResult.access_token,
          refresh_token: refreshResult.refresh_token,
          userId: refreshResult.userId,
        }),
      )

      const profileResult = await dispatch(
        authApiEndpoints.getAuthProfile.initiate(null, {
          forceRefetch: true,
        }),
      ).unwrap()

      const organizationName = profileResult?.organization?.name

      appLocalLogo.setData('logo', profileResult.organization.circularLogoURL)

      dispatch(
        setUserSession({
          user: profileResult.user,
          currentOrganization: profileResult.organization,
          membership: profileResult.membership,
          tokenPayload: profileResult.tokenPayload,
          policies: profileResult.policies,
        }),
      )

      dispatch(resetLeadStoreLead())
      dispatch(resetAccountStoreAccount())
      dispatch(rootApi.util.resetApiState())
      modals.closeAll()

      const ThemeTypeToLoad =
        organizationId === BENESTREAM_ORG_ID
          ? ThemeType.BENESTREAM
          : ThemeType.IDG

      loadTheme(ThemeTypeToLoad)

      // When the user is vendor, redirect to enrollment-checker page
      if (
        verifyVendorUserForBCF({
          organizationId,
          membershipId: profileResult?.membership?.membershipRole?.id,
        })
      ) {
        router.push(
          `/${cleanStringToBeUrlReadable(organizationName)}/enrollment-checker`,
        )
        return
      }

      if (personId) {
        router.push(
          `/${cleanStringToBeUrlReadable(organizationName)}/leads/${personId}`,
        )
        return
      }
      router.push(`/${cleanStringToBeUrlReadable(organizationName)}/leads`)
    } catch (err: any) {
      setError(err.message || 'An error occurred while changing organization')
      return {
        success: false,
        error: err.message,
      }
    } finally {
      setIsChanging(false)
      dispatch(setGlobalIsLoading(false))
    }
  }

  return {
    handleChangeOrganization,
    isChanging,
    error,
  }
}
